import { Builder } from "@builder.io/react";
import { selectableColors, allowedParagraphFontSizes, allowedBulletTypes, allowedImageRatios, allowedFileTypes, registerMenuGroups } from "./helper";
import { Heading, HeadingNoIndex, Label, Paragraph, Quote, ImageBlock, List, Table, VideoBlock, ContentBlock } from "../components/Contents";

const elementsGroups = [
  {
    name: "Content Components",
    items: [
      { name: "ContentBlock" },
      { name: "Heading" },
      { name: "HeadingNoIndex" },
      { name: "Label" },
      { name: "Paragraph" },
      { name: "Quote" },
      { name: "ImageBlock" },
      { name: "List" },
      { name: "Table" },
      { name: "VideoBlock" }
    ]
  }
];

export const registerElementsComponents = () => {
  registerMenuGroups(elementsGroups);
  // Contents Components
  Builder.registerComponent(ContentBlock, {
    name: "ContentBlock",
    friendlyName: "Content Block",
    noWrap: true,
    inputs: [
      {
        name: "topSpacing",
        type: "string",
        enum: ["none", "small", "medium", "large", "xlarge"],
        defaultValue: "small",
      },
      {
        name: "bottomSpacing",
        type: "string",
        enum: ["none", "small", "medium", "large", "xlarge"],
        defaultValue: "small",
      },
      {
        name: "content",
        type: "uiBlocks",
        defaultValue: [],
      },
    ],
  });

  Builder.registerComponent(Heading, {
    name: "Heading",
    friendlyName: "Heading (Indexed)",
    noWrap: true,
    inputs: [
      {
        name: "level",
        type: "string",
        enum: ["1", "2", "3", "4", "5", "6"],
        required: true,
      },
      {
        name: "title",
        type: "text",
      },
      {
        name: "color",
        type: "string",
        enum: selectableColors,
        defaultValue: "primary",
      },
      {
        name: "bold",
        type: "boolean",
        defaultValue: true,
      },
      {
        name: "showInTrackingPanel",
        type: "boolean",
        defaultValue: true,
      },
    ],
  });

  Builder.registerComponent(HeadingNoIndex, {
    name: "HeadingNoIndex",
    friendlyName: "Heading",
    noWrap: true,
    inputs: [
      {
        name: "level",
        type: "string",
        enum: ["1", "2", "3", "4", "5", "6"],
        required: true,
      },
      {
        name: "title",
        type: "text",
      },
      {
        name: "color",
        type: "string",
        enum: selectableColors,
        defaultValue: "primary",
      },
      {
        name: "bold",
        type: "boolean",
        defaultValue: true,
      },
    ],
  });

  Builder.registerComponent(Label, {
    name: "Label",
    noWrap: true,
    inputs: [
      {
        name: "label",
        type: "text",
        required: true,
      },
      {
        name: "color",
        type: "string",
        enum: selectableColors,
        defaultValue: "brand",
      },
      {
        name: "bold",
        type: "boolean",
        defaultValue: true,
      },
    ],
  });

  Builder.registerComponent(Paragraph, {
    name: "Paragraph",
    noWrap: true,
    inputs: [
      {
        name: "text",
        type: "richText",
        required: true,
      },
      {
        name: "fontSize",
        type: "string",
        enum: allowedParagraphFontSizes,
        defaultValue: "base",
      },
      {
        name: "textColor",
        type: "string",
        enum: selectableColors,
        defaultValue: "primary",
      },
      {
        name: "centerAlign",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "reducedBottomSpacing",
        type: "boolean",
        defaultValue: false,
      },
    ],
  });

  Builder.registerComponent(Quote, {
    name: "Quote",
    noWrap: true,
    inputs: [
      {
        name: "quote",
        type: "richText",
        required: true,
      },
      {
        name: "quoteAuthor",
        type: "text",
      },
      {
        name: "quoteAuthorLink",
        type: "url",
      },
      {
        name: "quoteSubline",
        type: "text",
      },
      
      
    ],
  });

  Builder.registerComponent(ImageBlock, {
    name: "ImageBlock",
    noWrap: true,
    inputs: [
      {
        name: "image",
        type: "file",
        allowedFileTypes: allowedFileTypes,
        defaultValue: "/placeholder.svg",
      },
      {
        name: "imageRatio",
        type: "string",
        enum: allowedImageRatios,
        defaultValue: "16/9",
        helperText: "Select 'open' to keep the image's natural proportions without forcing a fixed ratio.",
      },
      {
        name: "imageSize",
        type: "string",
        enum: ["50%", "75%", "100%"],
        defaultValue: "100%",
      },
      {
        name: "imageAlign",
        type: "string",
        enum: ["left", "center", "right"],
        defaultValue: "left",
      },
      {
        name: "altText",
        type: "text",
      },
      {
        name: "linkUrl",
        type: "url",
      },
      {
        name: "caption",
        type: "text",
      },
      {
        name: "captionAlign",
        type: "string",
        enum: ["left", "center", "right"],
        defaultValue: "left",
      },
      {
        name: "hasMobileImage",
        type: "boolean",
        defaultValue: false,
        helperText: "Enable to supply a separate image for mobile screens.",
      },
      {
        name: "mobileImage",
        type: "file",
        allowedFileTypes: allowedFileTypes,
        showIf: `options.get('hasMobileImage') === true`,
      },
      {
        name: "mobileImageAltText",
        type: "text",
        showIf: `options.get('hasMobileImage') === true`,
      },
    ],
  });

  Builder.registerComponent(VideoBlock, {
    name: "VideoBlock",
    noWrap: true,
    inputs: [
      {
        name: "video",
        type: "url",
      },
      {
        name: "image",
        type: "file",
        allowedFileTypes: allowedFileTypes,
        defaultValue: "/placeholder.svg",
      },
      {
        name: "imageRatio",
        type: "string",
        enum: allowedImageRatios,
        defaultValue: "16/9",
      },
      {
        name: "imageSize",
        type: "string",
        enum: ["50%", "75%", "100%"],
        defaultValue: "100%",
      },
      {
        name: "imageAlign",
        type: "string",
        enum: ["left", "center", "right"],
        defaultValue: "left",
      },
      {
        name: "altText",
        type: "text",
      },
      {
        name: "caption",
        type: "text",
      },
      {
        name: "captionAlign",
        type: "string",
        enum: ["left", "center", "right"],
        defaultValue: "left",
      },
    ],
  });

  Builder.registerComponent(List, {
    name: "List",
    noWrap: true,
    inputs: [
      {
        name: "title",
        type: "text",
      },
      {
        name: "withInnerSpacing",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "reducedTopSpacing",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "bulletType",
        type: "string",
        enum: allowedBulletTypes,
        defaultValue: "disc",
      },
      {
        name: "textColor",
        type: "string",
        enum: selectableColors,
        defaultValue: "primary",
      },
      {
        name: "fontSize",
        type: "string",
        enum: allowedParagraphFontSizes,
        defaultValue: "base",
      },
      {
        name: "listItems",
        type: "list",
        subFields: [
          {
            name: "item",
            type: "richText",
          },
        ],
        defaultValue: [
          { item: "<p>First item</p>" },
          { item: "<p>Second item</p>" },
        ],
      },
      
    ],
  });

  Builder.registerComponent(Table, {
    name: "Table",
    noWrap: true,
    inputs: [
      {
        name: "firstRowAsTableHeadingRow",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "boldTableContent",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "highlightEvenRows",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "horizontalBorder",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "verticalBorder",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "textAlignment",
        type: "string",
        enum: ["left", "center"],
        defaultValue: "left",
      },
      {
        name: "caption",
        type: "text",
      },
      {
        name: "row",
        type: "list",
        subFields: [
          {
            name: "col",
            type: "list",
            subFields: [
              {
                name: "col",
                type: "text",
              },
            ],
            defaultValue: [
              { item: "col11" },
              { item: "col12" },
              { item: "col13" },
            ],
          },
        ],
        defaultValue: [
          {
            col: [
              { col: "col11" },
              { col: "col12" },
              { col: "col13" },
            ],
          },
          {
            col: [
              { col: "col21" },
              { col: "col22" },
              { col: "col23" },
            ],
          },
          {
            col: [
              { col: "col31" },
              { col: "col32" },
              { col: "col33" },
            ],
          },
          {
            col: [
              { col: "col41" },
              { col: "col42" },
              { col: "col43" },
            ],
          },
          {
            col: [
              { col: "col51" },
              { col: "col52" },
              { col: "col53" },
            ],
          },
          {
            col: [
              { col: "col61" },
              { col: "col62" },
              { col: "col63" },
            ],
          },
        ],
      },
    ],
  });
};
